import apiClient from './apiClient';
import { API_ENDPOINTS } from '../config/apiConfig';
import {
  IngestionAuditRowResponse,
  IngestionHealthResponse,
  IngestionInvestigationFileResponse,
  IngestionLatencyMetricsResponse,
  IngestionLatestEventResponse,
  IngestionPagedResponse,
  IngestionQuarantineBulkRedriveResponse,
  IngestionQuarantineRedriveResultResponse,
  IngestionQuarantineRowResponse,
  IngestionRuntimeMetricsResponse,
  RtuFileActivityRowResponse,
} from '../types/IngestionObservability';

export interface LatestEventsQuery {
  limit?: number;
  from?: string;
  to?: string;
  rtuId?: string;
  fileType?: string;
  status?: string;
}

export interface InvestigationFilesQuery {
  limit?: number;
  outcome?: string;
}

export interface IngestionPageQuery {
  page?: number;
  size?: number;
  from?: string;
  to?: string;
  search?: string;
  rtuId?: string;
  fileType?: string;
  route?: string;
}

export interface InvestigationFilesPageQuery extends IngestionPageQuery {
  outcome?: string;
}

export interface RtuFileActivityPageQuery extends IngestionPageQuery {
  outcome?: string;
}

export interface AuditRowsPageQuery extends IngestionPageQuery {
  outcome?: string;
  eventType?: string;
  sourceComponent?: string;
}

export interface QuarantineRowsPageQuery extends IngestionPageQuery {
  errorCode?: string;
  status?: string;
}

const stripEmpty = <T extends object>(query: T) =>
  Object.fromEntries(
    Object.entries(query as Record<string, unknown>).filter(([, value]) => value !== undefined && value !== null && value !== '')
  ) as Record<string, string | number>;

const extractFilename = (contentDisposition: string | undefined, fallback = 'ingestion-artifact.bin') => {
  if (!contentDisposition) {
    return fallback;
  }
  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }
  const quotedMatch = contentDisposition.match(/filename="([^"]+)"/i);
  if (quotedMatch?.[1]) {
    return quotedMatch[1];
  }
  return fallback;
};

const downloadBlob = (blob: Blob, fileName: string) => {
  const objectUrl = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(objectUrl);
};

export const ingestionObservabilityService = {
  async fetchHealth(): Promise<IngestionHealthResponse> {
    const response = await apiClient.get<IngestionHealthResponse>(API_ENDPOINTS.INGESTION.HEALTH);
    return response.data;
  },

  async fetchRuntimeMetrics(): Promise<IngestionRuntimeMetricsResponse> {
    const response = await apiClient.get<IngestionRuntimeMetricsResponse>(API_ENDPOINTS.INGESTION.RUNTIME_METRICS);
    return response.data;
  },

  async fetchLatencyMetrics(): Promise<IngestionLatencyMetricsResponse> {
    const response = await apiClient.get<IngestionLatencyMetricsResponse>(API_ENDPOINTS.INGESTION.LATENCY_METRICS);
    return response.data;
  },

  async fetchLatestEvents(query: LatestEventsQuery = {}): Promise<IngestionLatestEventResponse[]> {
    const response = await apiClient.get<IngestionLatestEventResponse[]>(
      API_ENDPOINTS.INGESTION.LATEST_EVENTS,
      { params: stripEmpty(query) }
    );
    return response.data;
  },

  async fetchInvestigationFiles(query: InvestigationFilesQuery = {}): Promise<IngestionInvestigationFileResponse[]> {
    const response = await apiClient.get<IngestionInvestigationFileResponse[]>(
      API_ENDPOINTS.INGESTION.INVESTIGATION_FILES,
      { params: stripEmpty(query) }
    );
    return response.data;
  },

  async fetchAuditRows(limit = 25): Promise<IngestionAuditRowResponse[]> {
    const response = await apiClient.get<IngestionAuditRowResponse[]>(API_ENDPOINTS.INGESTION.AUDIT_LATEST, {
      params: stripEmpty({ limit }),
    });
    return response.data;
  },

  async fetchQuarantineRows(limit = 25): Promise<IngestionQuarantineRowResponse[]> {
    const response = await apiClient.get<IngestionQuarantineRowResponse[]>(API_ENDPOINTS.INGESTION.QUARANTINE_LATEST, {
      params: stripEmpty({ limit }),
    });
    return response.data;
  },

  async fetchInvestigationFilesPage(
    query: InvestigationFilesPageQuery = {}
  ): Promise<IngestionPagedResponse<IngestionInvestigationFileResponse>> {
    const response = await apiClient.get<IngestionPagedResponse<IngestionInvestigationFileResponse>>(
      API_ENDPOINTS.INGESTION.INVESTIGATION_FILES_PAGE,
      { params: stripEmpty(query) }
    );
    return response.data;
  },

  async fetchRtuFileActivityPage(
    query: RtuFileActivityPageQuery = {}
  ): Promise<IngestionPagedResponse<RtuFileActivityRowResponse>> {
    const response = await apiClient.get<IngestionPagedResponse<RtuFileActivityRowResponse>>(
      API_ENDPOINTS.INGESTION.FILE_ACTIVITY_PAGE,
      { params: stripEmpty(query) }
    );
    return response.data;
  },

  async fetchReceptionFileActivityPage(
    query: RtuFileActivityPageQuery = {}
  ): Promise<IngestionPagedResponse<RtuFileActivityRowResponse>> {
    const response = await apiClient.get<IngestionPagedResponse<RtuFileActivityRowResponse>>(
      API_ENDPOINTS.INGESTION.RECEPTION_FILE_ACTIVITY_PAGE,
      { params: stripEmpty(query) }
    );
    return response.data;
  },

  async fetchAuditRowsPage(query: AuditRowsPageQuery = {}): Promise<IngestionPagedResponse<IngestionAuditRowResponse>> {
    const response = await apiClient.get<IngestionPagedResponse<IngestionAuditRowResponse>>(
      API_ENDPOINTS.INGESTION.AUDIT_PAGE,
      { params: stripEmpty(query) }
    );
    return response.data;
  },

  async fetchQuarantineRowsPage(
    query: QuarantineRowsPageQuery = {}
  ): Promise<IngestionPagedResponse<IngestionQuarantineRowResponse>> {
    const response = await apiClient.get<IngestionPagedResponse<IngestionQuarantineRowResponse>>(
      API_ENDPOINTS.INGESTION.QUARANTINE_PAGE,
      { params: stripEmpty(query) }
    );
    return response.data;
  },

  async redriveQuarantineRow(id: number, reason?: string): Promise<IngestionQuarantineRedriveResultResponse> {
    const response = await apiClient.post<IngestionQuarantineRedriveResultResponse>(
      API_ENDPOINTS.INGESTION.QUARANTINE_REDRIVE(id),
      { reason }
    );
    return response.data;
  },

  async redriveQuarantineRows(ids: number[], reason: string): Promise<IngestionQuarantineBulkRedriveResponse> {
    const response = await apiClient.post<IngestionQuarantineBulkRedriveResponse>(
      API_ENDPOINTS.INGESTION.QUARANTINE_REDRIVE_BULK,
      { ids, reason }
    );
    return response.data;
  },

  async downloadInvestigationFile(correlationId: string): Promise<string> {
    const response = await apiClient.get<Blob>(
      API_ENDPOINTS.INGESTION.INVESTIGATION_FILE_DOWNLOAD(correlationId),
      { responseType: 'blob' }
    );
    const fileName = extractFilename(response.headers['content-disposition'], `${correlationId}.bin`);
    downloadBlob(response.data, fileName);
    return fileName;
  },
};

export default ingestionObservabilityService;
